import type { ContactFormStep, LoginMethod } from './constants';
import type { User } from './types';
import type { ContactFormModalProps } from '~/components/ContactFormModal';
import type { FormMeta } from 'vee-validate';

import { extractErrorLogContext } from '../useLogger';
import { CONTACT_FORM_STEPS, FORM_SUBMISSION_ACTIONS } from './constants';
import { useAuthService } from './useAuthService';
import { useContactFormAutofill } from './useContactFormAutofill';
import { useFormSubmissionStrategy } from './useFormSubmissionStrategy';
import { useLeadFlowLogger } from './useLeadFlowLogger';
import { useLeadService } from './useLeadService';

export interface ContactFormValues {
	name: string;
	phone: string;
	email?: string;
	userMessage?: string;
}

export interface ExternalFormSubmissionResult {
	success: boolean;
	step: ContactFormStep;
	/** Envio ignorado porque ja havia outro em andamento. Nao e falha. */
	skipped?: boolean;
}

export interface ContactFormModalState {
	// State refs
	currentStep: Ref<ContactFormStep>;
	errorMessage: Ref<string | null>;
	isSubmittingOtpForm: Ref<boolean>;
	isSubmittingForm: Ref<boolean>;
	isOTPValid: Ref<boolean | null>;

	// Props
	props: ContactFormModalProps;

	// Computed refs
	isPropertyContext: ComputedRef<boolean>;
	isUserLoggedIn: ComputedRef<boolean>;
	user: ComputedRef<User | null>;
	initialUserMessage: ComputedRef<string>;

	// Form field refs
	name: ComputedRef<string>;
	email: ComputedRef<string>;
	phone: ComputedRef<string>;
	userMessage: ComputedRef<string>;
	meta: ComputedRef<FormMeta<ContactFormValues>>;

	// Form methods
	validate: () => Promise<{ valid: boolean }>;
	setFormSubmittedSuccessfully: (value: boolean) => void;
	fetchUserSession: () => Promise<void>;
	getAdCampaign: () => Record<string, string>;
	handleSubmit: (
		onSubmit: (values: ContactFormValues, ctx?: unknown) => Promise<void>
	) => (e?: Event) => Promise<Promise<void>>;
}

const ANALYTICS_BY_CONTEXT = {
	property: {
		leadContext: 'property',
		gtmLabel: 'property_contact_form',
		gtmFormId: 'property-price-contact-form'
	},
	condominium: {
		leadContext: 'condo',
		gtmLabel: 'condo_contact_form',
		gtmFormId: 'contact-form-section'
	},
	agent: {
		leadContext: 'agent',
		gtmLabel: 'agent_contact_form',
		gtmFormId: 'agent-contact-form'
	}
} as const;

export function useContactFormActions(modalState: ContactFormModalState) {
	const gtm = useGtm();
	const authService = useAuthService();
	const leadService = useLeadService();
	const { getSubmissionAction } = useFormSubmissionStrategy();
	const logger = useLeadFlowLogger('ContactFormActions');
	const { getAutofillSnapshot } = useContactFormAutofill();

	function getLeadContext() {
		return {
			context: ANALYTICS_BY_CONTEXT[modalState.props.context].leadContext,
			agentSlug: modalState.props.agentSlug,
			propertyId: modalState.props.propertyId,
			propertyCommercialId: modalState.props.propertyCommercialId,
			condoId: modalState.props.condoId,
			condoName: modalState.props.condoName,
			currentStep: modalState.currentStep.value,
			isUserLoggedIn: modalState.isUserLoggedIn.value,
			loginMethod: modalState.user.value?.login_method,
			hasCompletedRegistration:
				modalState.user.value?.hasCompletedRegistration ?? false
		};
	}

	function trackRumAction(name: string, context: Record<string, unknown> = {}) {
		logger.rumAction(name, { ...getLeadContext(), ...context });
	}

	function reportRumError(
		error: unknown,
		context: Record<string, unknown> = {}
	) {
		logger.rumError(error, { ...getLeadContext(), ...context });
	}

	// Um form_submit por ciclo. Dois envios da mesma intencao (bus da caixa
	// de preco + botao do modal) contavam dois leads em todas as plataformas.
	const hasTrackedFormSubmit = ref(false);

	function trackGTMFormSubmit() {
		if (hasTrackedFormSubmit.value) {
			trackRumAction('lead_submit_blocked_concurrent', { entry: 'gtm_push' });
			return;
		}

		if (!gtm) {
			logger.debug('GTM indisponível — evento form_submit não enviado', {
				...getLeadContext()
			});
			return;
		}

		const autofill = getAutofillSnapshot();

		try {
			gtm.trackEvent({
				event: 'form_submit',
				action: 'form_submit',
				label: ANALYTICS_BY_CONTEXT[modalState.props.context].gtmLabel,
				form_id: ANALYTICS_BY_CONTEXT[modalState.props.context].gtmFormId,
				form_name: modalState.name.value,
				form_phone: modalState.phone.value,
				form_email: modalState.email.value,
				form_message: modalState.userMessage.value,
				lead_created: true,
				form_context:
					ANALYTICS_BY_CONTEXT[modalState.props.context].leadContext,
				property_id: modalState.props.propertyCommercialId || null,
				property_price: modalState.props.askingPrice || null,
				currency: modalState.props.askingPrice ? 'BRL' : null,
				autofill_detected: autofill.hasDetectedAutofill,
				autofill_fields: autofill.autofilledFields,
				instagram_browser: autofill.isInstagramBrowser,
				autofill_instagram_session: autofill.isAutofillInInstagramSession
			});

			hasTrackedFormSubmit.value = true;
		} catch (error) {
			logger.warn('Falha ao enviar evento form_submit para GTM', {
				...getLeadContext(),
				...extractErrorLogContext(error)
			});
		}
	}

	function goToContactFormStep() {
		hasTrackedFormSubmit.value = false;
		modalState.currentStep.value = CONTACT_FORM_STEPS.CONTACT_FORM;
		modalState.setFormSubmittedSuccessfully(false);

		nextTick(async () => {
			await modalState.validate();
		});
	}

	function goToSuccessMessage() {
		modalState.currentStep.value = CONTACT_FORM_STEPS.SUCCESS_MESSAGE;
		modalState.setFormSubmittedSuccessfully(true);
		trackGTMFormSubmit();
	}

	function goToErrorMessage(error?: string) {
		modalState.currentStep.value = CONTACT_FORM_STEPS.ERROR_MESSAGE;
		if (error) {
			modalState.errorMessage.value = error;
		}
	}

	async function goToOtpFormStep(shouldSendCode = false) {
		modalState.currentStep.value = CONTACT_FORM_STEPS.OTP_FORM;

		if (shouldSendCode && modalState.phone.value) {
			await authService.sendOtpCode(modalState.phone.value);
		}
	}

	async function submitLead(values: ContactFormValues): Promise<boolean> {
		const adCampaign = modalState.getAdCampaign();
		const leadData = {
			name: values.name || (modalState.user.value?.name ?? '') || '',
			phone: values.phone,
			email: values.email || (modalState.user.value?.email ?? undefined),
			message:
				values.userMessage?.trim() || modalState.initialUserMessage.value,
			adCampaign
		};

		logger.info('Submetendo lead', {
			...getLeadContext(),
			hasAdCampaign: Object.keys(adCampaign || {}).length > 0
		});

		// switch e não ternário: com dois ramos, qualquer contexto novo caía no de
		// condomínio e o lead seria criado com `condoId` indefinido.
		const result = await (() => {
			switch (modalState.props.context) {
				case 'property':
					return leadService.createPropertyLead({
						...leadData,
						propertyId: modalState.props.propertyId!
					});
				case 'agent':
					// Mesmo endpoint do imóvel: muda só a chave da entidade.
					return leadService.createPropertyLead({
						...leadData,
						agentSlug: modalState.props.agentSlug!
					});
				case 'condominium':
					return leadService.createCondoLead({
						...leadData,
						condoId: modalState.props.condoId!,
						condoName: modalState.props.condoName ?? '',
						companyDistribution: modalState.props.companyDistribution!
					});
			}
		})();

		if (!result.success) {
			logger.warn('Submissão de lead falhou', {
				...getLeadContext(),
				error: result.error
			});
			trackRumAction('lead_submission_failed', { error: result.error });
			goToErrorMessage(result.error);
			return false;
		}

		logger.info('Submissão de lead concluída com sucesso', {
			...getLeadContext()
		});
		trackRumAction('lead_submission_success');
		goToSuccessMessage();
		return true;
	}

	const handleFormSubmit = modalState.handleSubmit(
		async (values: ContactFormValues) => {
			try {
				await submitLead(values);
			} catch (error) {
				logger.error('Exceção não esperada durante submitLead', {
					...getLeadContext(),
					...extractErrorLogContext(error)
				});
				reportRumError(error, { stage: 'submitLead' });
				const errorConverted = error as Error;
				goToErrorMessage(errorConverted?.message);
				return Promise.resolve();
			}
		}
	);

	async function completePhoneRegistration(): Promise<boolean> {
		const result = await authService.completePhoneRegistration(
			modalState.name.value,
			modalState.email.value || undefined
		);

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		await modalState.fetchUserSession();
		return true;
	}

	async function completeSocialRegistration(): Promise<boolean> {
		const result = await authService.completeSocialRegistration(
			modalState.phone.value
		);

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		await modalState.fetchUserSession();

		const otpResult = await authService.sendOtpCode(modalState.phone.value);
		if (!otpResult.success) {
			goToErrorMessage(otpResult.error);
			return false;
		}

		await goToOtpFormStep();
		return true;
	}

	async function startOtpFlow(): Promise<boolean> {
		const result = await authService.sendOtpCode(modalState.phone.value);

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		await goToOtpFormStep();
		return true;
	}

	async function sendForm() {
		if (modalState.isSubmittingForm.value) {
			trackRumAction('lead_submit_blocked_concurrent', { entry: 'modal' });
			return;
		}

		hasTrackedFormSubmit.value = false;

		logger.info('sendForm chamado', { ...getLeadContext() });
		trackRumAction('lead_form_submit_started');
		try {
			modalState.isSubmittingForm.value = true;

			await modalState.validate();
			if (!modalState.meta.value.valid) {
				logger.warn('sendForm abortado por validação inválida', {
					...getLeadContext(),
					errors: modalState.meta.value.valid
						? {}
						: Object.keys(
								(modalState.meta.value as unknown as { errors?: object })
									.errors ?? {}
							)
				});
				return;
			}

			const action = getSubmissionAction({
				isLoggedIn: modalState.isUserLoggedIn.value,
				loginMethod: modalState.user.value?.login_method as
					| LoginMethod
					| undefined,
				hasCompletedRegistration:
					modalState.user.value?.hasCompletedRegistration ?? false
			});

			logger.info('Estratégia de submissão determinada', {
				...getLeadContext(),
				action
			});

			let success = false;

			switch (action) {
				case FORM_SUBMISSION_ACTIONS.SUBMIT:
					await handleFormSubmit();
					success = true;
					break;

				case FORM_SUBMISSION_ACTIONS.COMPLETE_PHONE:
					success = await completePhoneRegistration();
					if (success) {
						await handleFormSubmit();
					}
					break;

				case FORM_SUBMISSION_ACTIONS.COMPLETE_SOCIAL:
					success = await completeSocialRegistration();
					break;

				case FORM_SUBMISSION_ACTIONS.OTP:
					success = await startOtpFlow();
					break;
			}

			logger.info('sendForm finalizado', {
				...getLeadContext(),
				action,
				success
			});
		} catch (error) {
			logger.error('Exceção não esperada em sendForm', {
				...getLeadContext(),
				...extractErrorLogContext(error)
			});
			reportRumError(error, { stage: 'sendForm' });
			goToErrorMessage((error as Error)?.message);
		} finally {
			modalState.isSubmittingForm.value = false;
		}
	}

	async function submitOtp(otp: string, isSocialUser: boolean) {
		if (modalState.isSubmittingOtpForm.value) {
			logger.debug('submitOtp ignorado — submissão já em andamento');
			return;
		}

		logger.info('Iniciando validação de OTP', {
			...getLeadContext(),
			isSocialUser
		});

		try {
			modalState.isSubmittingOtpForm.value = true;

			const result = isSocialUser
				? await authService.validateSocialOtp(otp)
				: await authService.validatePhoneOtp({
						phoneNumber: modalState.phone.value,
						otp,
						email: modalState.email.value || undefined,
						name: modalState.name.value || undefined
					});

			if (!result.success) {
				logger.warn('Validação de OTP falhou', {
					...getLeadContext(),
					isSocialUser,
					error: result.error
				});
				modalState.isOTPValid.value = false;
				return;
			}

			modalState.isOTPValid.value = true;

			await modalState.fetchUserSession();
			logger.info('OTP validado — submetendo lead após fetchUserSession', {
				...getLeadContext(),
				isSocialUser
			});
			try {
				modalState.isSubmittingForm.value = true;
				await handleFormSubmit();
			} finally {
				modalState.isSubmittingForm.value = false;
			}
		} catch (error) {
			logger.error('Exceção não esperada durante submitOtp', {
				...getLeadContext(),
				isSocialUser,
				...extractErrorLogContext(error)
			});
			reportRumError(error, { stage: 'submitOtp', isSocialUser });
			const errorConverted = error as Error;
			goToErrorMessage(errorConverted?.message);
		} finally {
			modalState.isSubmittingOtpForm.value = false;
		}
	}

	async function handleOtpFormValidation(otp: string): Promise<void> {
		const isSocialUser =
			modalState.isUserLoggedIn.value &&
			(modalState.user.value?.login_method === 'google' ||
				modalState.user.value?.login_method === 'facebook');

		try {
			await submitOtp(otp, isSocialUser);
		} catch (error) {
			logger.error('Erro não capturado em handleOtpFormValidation', {
				...getLeadContext(),
				isSocialUser,
				...extractErrorLogContext(error)
			});
			reportRumError(error, {
				stage: 'handleOtpFormValidation',
				isSocialUser
			});
			goToErrorMessage((error as Error)?.message);
		}
	}

	async function resendOtp(): Promise<void> {
		logger.info('Reenviando código OTP', { ...getLeadContext() });
		modalState.isOTPValid.value = null;
		const result = await authService.sendOtpCode(modalState.phone.value);
		if (!result.success) {
			logger.warn('Reenvio de OTP falhou', {
				...getLeadContext(),
				error: result.error
			});
			goToErrorMessage(result.error);
		}
	}

	async function submitExternalForm(): Promise<ExternalFormSubmissionResult> {
		if (modalState.isSubmittingForm.value) {
			trackRumAction('lead_submit_blocked_concurrent', {
				entry: 'price_box'
			});
			return {
				success: false,
				step: modalState.currentStep.value,
				skipped: true
			};
		}

		hasTrackedFormSubmit.value = false;

		logger.info('submitExternalForm chamado', { ...getLeadContext() });
		try {
			modalState.isSubmittingForm.value = true;

			await modalState.validate();
			if (!modalState.meta.value.valid) {
				logger.warn('submitExternalForm abortado por validação inválida', {
					...getLeadContext()
				});
				return { success: false, step: CONTACT_FORM_STEPS.CONTACT_FORM };
			}

			const values = {
				name: modalState.name.value,
				phone: modalState.phone.value,
				email: modalState.email.value,
				userMessage: modalState.userMessage.value
			};

			const success = await submitLead(values);

			return {
				success,
				step: success
					? CONTACT_FORM_STEPS.SUCCESS_MESSAGE
					: CONTACT_FORM_STEPS.ERROR_MESSAGE
			};
		} catch (error) {
			logger.error('Exceção não esperada em submitExternalForm', {
				...getLeadContext(),
				...extractErrorLogContext(error)
			});
			reportRumError(error, { stage: 'submitExternalForm' });
			const errorConverted = error as Error;
			goToErrorMessage(errorConverted?.message);
			return {
				success: false,
				step: CONTACT_FORM_STEPS.ERROR_MESSAGE
			};
		} finally {
			modalState.isSubmittingForm.value = false;
		}
	}

	return {
		goToContactFormStep,
		goToSuccessMessage,
		goToErrorMessage,
		goToOtpFormStep,
		sendForm,
		submitForm: handleFormSubmit,
		submitExternalForm,
		resendOtp,
		handleOtpFormValidation
	};
}
